import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Search, Download, Send, ExternalLink, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

const SENIORITY_OPTIONS = [
  { v: "c_suite", l: "C-Suite" },
  { v: "vp", l: "VP" },
  { v: "head", l: "Head" },
  { v: "director", l: "Director" },
  { v: "manager", l: "Manager" },
  { v: "owner", l: "Owner / Founder" },
];

const EMP_BANDS = [
  { v: "1,10", l: "1–10" },
  { v: "11,50", l: "11–50" },
  { v: "51,200", l: "51–200" },
  { v: "201,500", l: "201–500" },
  { v: "501,1000", l: "501–1000" },
  { v: "1001,5000", l: "1001–5000" },
  { v: "5001,10000", l: "5001–10k" },
  { v: "10001,1000000", l: "10k+" },
];

// Official Lusha contact departments (GET /prospecting/filters/contacts/departments)
const LUSHA_DEPARTMENTS = [
  "Finance",
  "General Management",
  "Operations",
  "Sales",
  "Marketing",
  "Information Technology",
  "Engineering & Technical",
  "Human Resources",
  "Legal",
  "Product",
  "Research & Analytics",
  "Customer Service",
  "Business Development",
  "Consulting",
  "Health Care & Medical",
  "Other",
];

export default function LeadFinder() {
  const [provider, setProvider] = useState("apollo");
  const [apolloStatus, setApolloStatus] = useState({ connected: false });
  const [lushaStatus, setLushaStatus] = useState({ connected: false });
  const [apifyStatus, setApifyStatus] = useState({ connected: false });
  const [lushaRequestId, setLushaRequestId] = useState(null);
  const [lushaCredits, setLushaCredits] = useState(null);
  const [instantlyStatus, setInstantlyStatus] = useState({ connected: false });
  const [titles, setTitles] = useState("CFO, Chief Financial Officer, Finance Director");
  const [locations, setLocations] = useState("Australia");
  const [orgLocations, setOrgLocations] = useState("Australia");
  const [domains, setDomains] = useState("");
  const [seniorities, setSeniorities] = useState(["c_suite", "director"]);
  const [empBands, setEmpBands] = useState(["51,200", "201,500", "501,1000"]);
  const [departments, setDepartments] = useState(["Finance"]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [pushCampaign, setPushCampaign] = useState("");
  const [pushing, setPushing] = useState(false);

  // Sales Nav (Apify) state
  const [snUrl, setSnUrl] = useState("");
  const [snMaxResults, setSnMaxResults] = useState(50);
  const [snAutoEnrich, setSnAutoEnrich] = useState(true);
  const [snAutoEnroll, setSnAutoEnroll] = useState(true);
  const [snRunning, setSnRunning] = useState(false);
  const [snRun, setSnRun] = useState(null);
  const [snRuns, setSnRuns] = useState([]);

  useEffect(() => {
    api.get("/apollo/status").then((r) => setApolloStatus(r.data)).catch(() => {});
    api.get("/lusha/status").then((r) => setLushaStatus(r.data)).catch(() => {});
    api.get("/apify/status").then((r) => setApifyStatus(r.data)).catch(() => {});
    api.get("/instantly/status").then((r) => {
      setInstantlyStatus(r.data);
      if (r.data?.default_campaign_id) setPushCampaign(r.data.default_campaign_id);
    }).catch(() => {});
    api.get("/apify/sales-nav/runs", { params: { limit: 5 } }).then((r) => setSnRuns(r.data || [])).catch(() => {});
  }, []);

  // Poll the active sales-nav run every 5s until it's done
  useEffect(() => {
    if (!snRun || ["succeeded", "failed"].includes(snRun.status)) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/apify/sales-nav/runs/${snRun.run_id}`);
        setSnRun(data);
        if (["succeeded", "failed"].includes(data.status)) {
          clearInterval(t);
          api.get("/apify/sales-nav/runs", { params: { limit: 5 } }).then((r) => setSnRuns(r.data || [])).catch(() => {});
          if (data.status === "succeeded") {
            toast.success(`Scrape complete · imported ${data.stats?.imported || 0} · enrolled ${data.stats?.enrolled || 0}`);
          } else {
            toast.error(`Scrape failed: ${data.error || data.apify_status}`);
          }
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(t);
  }, [snRun]);

  const providerConnected = provider === "apollo" ? apolloStatus.connected
    : provider === "lusha" ? lushaStatus.connected
    : apifyStatus.connected;

  const toggleSeniority = (v) => setSeniorities((arr) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const toggleEmp = (v) => setEmpBands((arr) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const splitCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const runSearch = async (newPage = 1) => {
    if (!providerConnected) {
      toast.error(`${provider === "apollo" ? "Apollo" : "Lusha"} not connected — paste your API key in Settings`);
      return;
    }
    setLoading(true);
    try {
      let data;
      if (provider === "apollo") {
        const body = {
          page: newPage,
          per_page: perPage,
          person_titles: splitCsv(titles),
          person_locations: splitCsv(locations),
          organization_locations: splitCsv(orgLocations),
          person_seniorities: seniorities,
          organization_num_employees_ranges: empBands,
        };
        const dlist = splitCsv(domains);
        if (dlist.length) body.q_organization_domains_list = dlist;
        const r = await api.post("/apollo/search", body);
        data = r.data;
      } else {
        // Lusha — new prospecting API (seniority + dept + countries by name)
        // Lusha wants full country names, not ISO-2
        const COUNTRY_MAP = { au: "Australia", nz: "New Zealand", us: "United States", gb: "United Kingdom", uk: "United Kingdom", in: "India", sg: "Singapore", ca: "Canada" };
        const countries = splitCsv(locations).map((c) => {
          const t = c.trim();
          if (t.length === 2) return COUNTRY_MAP[t.toLowerCase()] || t;
          return t; // assume already a full name like "Australia"
        });
        // Map seniorities → Lusha enum names (backend also accepts numeric IDs)
        const senMap = { c_level: "c-suite", c_suite: "c-suite", founder: "founder", vp: "vp", director: "director", manager: "manager", senior: "senior", entry: "entry" };
        const lushaSeniorities = seniorities.map((s) => senMap[s] || s);
        // Send each employee bracket as a separate size entry so Lusha treats them as OR
        const sizes = empBands.map((b) => {
          const [mn, mx] = b.split(",").map((x) => parseInt(x, 10));
          return { min: mn, max: mx };
        });
        const body = {
          page: Math.max(0, (newPage || 1) - 1), // Lusha uses 0-based pages
          page_size: Math.max(10, Math.min(40, perPage)),
          seniorities: lushaSeniorities.length ? lushaSeniorities : ["c-suite", "director"],
          countries,
          ...(departments.length ? { departments } : {}),
          // Send each employee bracket as separate {min,max} so Lusha OR's them
          ...(sizes.length ? { employee_sizes: sizes } : {}),
        };
        const r = await api.post("/lusha/search", body);
        data = r.data;
        setLushaRequestId(data.request_id);
      }
      setResults(data.results || []);
      setPagination(data.pagination || null);
      setPage(newPage);
      setSelected({});
      const total = data.pagination?.total_entries ?? data.pagination?.total ?? data.results?.length ?? 0;
      toast.success(`Found ${total} matches via ${provider}${provider === "lusha" ? " — emails revealed on import" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${provider} search failed`);
    } finally {
      setLoading(false);
    }
  };

  const rowKey = (r) => r.apollo_id || r.lusha_id || r.email;

  const toggleAll = () => {
    const allOn = results.length > 0 && results.every((r) => selected[rowKey(r)]);
    if (allOn) setSelected({});
    else {
      const next = {};
      for (const r of results) next[rowKey(r)] = true;
      setSelected(next);
    }
  };

  const selectedRows = () => results.filter((r) => selected[rowKey(r)]);

  const importLeads = async () => {
    const rows = selectedRows();
    if (!rows.length) {
      toast.error("Select at least one row");
      return;
    }
    setImporting(true);
    try {
      let importPayload = rows;
      if (provider === "lusha") {
        // Step 1: reveal emails + phones for selected rows (consumes credits)
        if (!lushaRequestId) {
          toast.error("Run a fresh search before importing — request expired");
          setImporting(false);
          return;
        }
        const lusha_ids = rows.map((r) => r.lusha_id).filter(Boolean);
        const { data: rev } = await api.post("/lusha/reveal", {
          request_id: lushaRequestId,
          lusha_ids,
          reveal_emails: true,
          reveal_phones: true,
        });
        importPayload = rev.revealed || [];
        setLushaCredits(rev.remaining_credits ?? null);
        toast.success(`Revealed ${importPayload.length} contacts · ${rev.credits_used} credits used · ${rev.remaining_credits ?? "—"} left`);
      }
      const endpoint = provider === "apollo" ? "/apollo/import" : "/lusha/import";
      const { data } = await api.post(endpoint, { results: importPayload });
      const parts = [];
      if (data.created) parts.push(`${data.created} new`);
      if (data.duplicates) parts.push(`${data.duplicates} already in your leads`);
      if (data.skipped_no_email) parts.push(`${data.skipped_no_email} no email available`);
      const summary = parts.join(" · ") || "no changes";
      if (data.created > 0) {
        toast.success(`Imported ${data.created} new CFO${data.created > 1 ? "s" : ""} into your leads · ${summary}`);
      } else if (data.duplicates > 0 && !data.skipped_no_email) {
        toast.success(`All ${data.duplicates} contacts were already in your leads (no duplicates created) — open /leads to see them`);
      } else {
        toast.success(summary);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const loadCampaigns = async () => {
    if (!instantlyStatus.connected) {
      toast.error("Instantly not connected — paste your API V2 key in Settings");
      return;
    }
    try {
      const { data } = await api.get("/instantly/campaigns");
      setCampaigns(data.campaigns || []);
      if (!pushCampaign && data.campaigns?.[0]) setPushCampaign(data.campaigns[0].id);
      toast.success(`Loaded ${data.campaigns?.length || 0} campaigns`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load campaigns");
    }
  };

  const pushToInstantly = async () => {
    const rows = selectedRows().filter((r) => r.email);
    if (!rows.length) {
      toast.error("Select rows with email addresses");
      return;
    }
    if (!pushCampaign) {
      toast.error("Pick an Instantly campaign first");
      return;
    }
    setPushing(true);
    try {
      // Step 1: import to local DB so they have lead_ids
      const { data: imp } = await api.post("/apollo/import", { results: rows });
      // Step 2: fetch fresh leads by email & push each to Instantly
      let pushed = 0; let failed = 0;
      for (const r of rows) {
        try {
          // find local lead
          const lookup = await api.get("/leads", { params: { q: r.email, limit: 1 } });
          const ld = (lookup.data || []).find((x) => (x.email || "").toLowerCase() === r.email.toLowerCase());
          if (!ld) { failed += 1; continue; }
          await api.post("/instantly/add-lead", { lead_id: ld.lead_id, campaign_id: pushCampaign });
          pushed += 1;
        } catch { failed += 1; }
      }
      toast.success(`Imported ${imp.created} · pushed ${pushed} to Instantly · ${failed} failed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Push failed");
    } finally {
      setPushing(false);
    }
  };

  return (
    <Layout title="Lead Finder" subtitle={`Source: ${provider === "apollo" ? "Apollo.io" : provider === "lusha" ? "Lusha (ANZ)" : "LinkedIn Sales Navigator (via Apify)"}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* Filters */}
        <aside className="space-y-4">
          <div className="bg-card border border-border p-3" style={{ borderRadius: 2 }} data-testid="lf-provider-toggle">
            <Label className="overline">Lead source</Label>
            <Select value={provider} onValueChange={(v) => { setProvider(v); setResults([]); setSelected({}); setLushaRequestId(null); }}>
              <SelectTrigger className="mt-1 input-crisp"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apollo">Apollo.io (free 50/mo, global)</SelectItem>
                <SelectItem value="lusha">Lusha (Premium req'd, best for ANZ)</SelectItem>
                <SelectItem value="salesnav">LinkedIn Sales Nav (Apify · automated)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-2">
              {provider === "lusha"
                ? "Lusha charges 1 credit per email + 10 per phone on reveal. Search is free."
                : provider === "salesnav"
                  ? "Apify scrapes your Sales Nav search URL (no LinkedIn cookie needed), Lusha reveals each row's email + phone, and every imported lead auto-enrols into the 11-step CFO sequence."
                  : "Apollo emails are included in your plan's monthly credit."}
            </div>
          </div>

          <ConnectionPill
            label={provider === "apollo" ? "Apollo" : provider === "lusha" ? "Lusha" : "Apify"}
            ok={providerConnected}
            testid={provider === "apollo" ? "apollo-conn" : provider === "lusha" ? "lusha-conn" : "apify-conn"}
          />

          {provider === "salesnav" ? (
            <SalesNavPanel
              snUrl={snUrl} setSnUrl={setSnUrl}
              snMaxResults={snMaxResults} setSnMaxResults={setSnMaxResults}
              snAutoEnrich={snAutoEnrich} setSnAutoEnrich={setSnAutoEnrich}
              snAutoEnroll={snAutoEnroll} setSnAutoEnroll={setSnAutoEnroll}
              snRunning={snRunning} setSnRunning={setSnRunning}
              snRun={snRun} setSnRun={setSnRun}
              apifyConnected={apifyStatus.connected}
              lushaConnected={lushaStatus.connected}
            />
          ) : (
          <div className="bg-card border border-border p-5 space-y-4" style={{ borderRadius: 2 }}>
            <div>
              <Label className="overline">Titles (comma-separated)</Label>
              <Input value={titles} onChange={(e) => setTitles(e.target.value)} className="mt-1 input-crisp" data-testid="lf-titles" />
            </div>
            <div>
              <Label className="overline">Person locations</Label>
              <Input value={locations} onChange={(e) => setLocations(e.target.value)} className="mt-1 input-crisp" data-testid="lf-person-locations" />
            </div>
            <div>
              <Label className="overline">Organisation locations</Label>
              <Input value={orgLocations} onChange={(e) => setOrgLocations(e.target.value)} className="mt-1 input-crisp" data-testid="lf-org-locations" />
            </div>
            <div>
              <Label className="overline">Org domains (optional)</Label>
              <Input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="bluescope.com, qantas.com" className="mt-1 input-crisp" data-testid="lf-domains" />
            </div>

            <div>
              <Label className="overline">Seniority</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {SENIORITY_OPTIONS.map((o) => (
                  <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`lf-sen-${o.v}`}>
                    <Checkbox checked={seniorities.includes(o.v)} onCheckedChange={() => toggleSeniority(o.v)} />
                    {o.l}
                  </label>
                ))}
              </div>
            </div>

            {provider === "lusha" && (
              <div>
                <Label className="overline">Department</Label>
                <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                  Lusha matches by department + seniority (not free-text titles). Tick <strong>Finance</strong> to get CFOs / Finance Directors only.
                </p>
                <div className="grid grid-cols-2 gap-1.5 mt-2 max-h-56 overflow-y-auto pr-1">
                  {LUSHA_DEPARTMENTS.map((d) => (
                    <label key={d} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`lf-dept-${d.replace(/\s+/g, "-").toLowerCase()}`}>
                      <Checkbox
                        checked={departments.includes(d)}
                        onCheckedChange={() => setDepartments((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d])}
                      />
                      <span className="truncate">{d}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="overline">Employees</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {EMP_BANDS.map((o) => (
                  <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`lf-emp-${o.v}`}>
                    <Checkbox checked={empBands.includes(o.v)} onCheckedChange={() => toggleEmp(o.v)} />
                    {o.l}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Per page</Label>
                <Select value={String(perPage)} onValueChange={(v) => setPerPage(Number(v))}>
                  <SelectTrigger className="mt-1 input-crisp" data-testid="lf-per-page"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={() => runSearch(1)} disabled={loading} className="w-full" data-testid="lf-search" style={{ borderRadius: 2 }}>
                  <Search className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                  {loading ? "Searching…" : `Search ${provider === "apollo" ? "Apollo" : "Lusha"}`}
                </Button>
              </div>
            </div>
          </div>
          )}

          <div className="bg-card border border-border p-5 space-y-3" style={{ borderRadius: 2 }}>
            <ConnectionPill label="Instantly" ok={instantlyStatus.connected} testid="instantly-conn" />
            {instantlyStatus.connected ? (
              <>
                <div className="flex items-center gap-2">
                  <Select value={pushCampaign} onValueChange={setPushCampaign}>
                    <SelectTrigger className="input-crisp text-xs" data-testid="lf-campaign-select">
                      <SelectValue placeholder="Pick a campaign…" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.status ? `· ${c.status}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={loadCampaigns} data-testid="lf-load-campaigns" style={{ borderRadius: 2 }}>Load</Button>
                </div>
                {!campaigns.length && instantlyStatus.default_campaign_id && (
                  <div className="text-xs text-muted-foreground">Using default campaign from Settings.</div>
                )}
              </>
            ) : (
              <div className="text-xs text-muted-foreground">Add your Instantly key in <Link to="/settings" className="underline">Settings</Link> to push directly into a campaign.</div>
            )}
          </div>

          {provider === "apollo" && (
            <div className="bg-card border border-border p-5 space-y-3" style={{ borderRadius: 2 }}>
              <div className="overline">CSV import (trial-friendly)</div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Apollo's API search is paid-tier-only. On <strong className="text-foreground">trial</strong>, export your CFO search as CSV from Apollo's web UI and upload it here. AU + non-govt/edu filters auto-applied.
              </div>
              <input
                type="file"
                accept=".csv,text/csv"
                data-testid="apollo-csv-upload"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    toast.loading("Uploading…", { id: "apollo-csv" });
                    const { data } = await api.post("/apollo/import-csv", fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    toast.success(
                      `Created ${data.created} leads · ${data.enrolled} enrolled · ${data.duplicates} dupes · ${data.out_of_region} non-AU · ${data.excluded_industry} excluded`,
                      { id: "apollo-csv", duration: 8000 }
                    );
                    e.target.value = "";
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Upload failed", { id: "apollo-csv" });
                  }
                }}
                className="text-xs w-full file:mr-3 file:py-1.5 file:px-3 file:border file:border-border file:bg-secondary file:text-foreground file:text-xs hover:file:bg-secondary/70 cursor-pointer"
              />
              <div className="text-xs text-muted-foreground">
                Expected columns (any case): <code className="text-foreground">email</code>, <code className="text-foreground">first_name</code>, <code className="text-foreground">last_name</code>, <code className="text-foreground">title</code>, <code className="text-foreground">company</code>, <code className="text-foreground">industry</code>, <code className="text-foreground">linkedin_url</code>. Apollo's default export already includes these.
              </div>
            </div>
          )}
        </aside>

        {/* Results */}
        <section className="space-y-4 min-w-0">
          {!providerConnected && (
            <div className="border border-border bg-card p-4 flex items-start gap-3" style={{ borderRadius: 2 }} data-testid={provider === "apollo" ? "apollo-not-connected" : provider === "lusha" ? "lusha-not-connected" : "apify-not-connected"}>
              <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5" strokeWidth={1.5} />
              <div className="text-sm">
                {provider === "apollo" ? "Apollo" : provider === "lusha" ? "Lusha" : "Apify"} not connected. <Link to="/settings" className="underline">Add your API key in Settings</Link> — you can set this later, the rest of the engine works without it.
              </div>
            </div>
          )}

          {provider === "salesnav" ? (
            <SalesNavResults snRun={snRun} snRuns={snRuns} lushaConnected={lushaStatus.connected} />
          ) : (
          <>
          {provider === "lusha" && lushaCredits != null && (
            <div className="text-xs text-muted-foreground" data-testid="lf-lusha-credits">
              Lusha credits left: <span className="font-mono text-foreground">{lushaCredits}</span>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-muted-foreground" data-testid="lf-results-count">
              {pagination ? `Page ${pagination.page} of ${pagination.total_pages ?? Math.ceil((pagination.total ?? pagination.total_entries ?? 0) / (pagination.page_size ?? perPage))} · ${(pagination.total_entries ?? pagination.total ?? 0).toLocaleString?.() ?? pagination.total} matches` : `Run a search to see CFO leads from ${provider === "apollo" ? "Apollo" : "Lusha"}`}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={importLeads} disabled={importing || selectedRows().length === 0} data-testid="lf-import" style={{ borderRadius: 2 }}>
                <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {provider === "lusha" ? "Reveal & Import" : "Import"} {selectedRows().length || ""}
              </Button>
              <Button size="sm" onClick={pushToInstantly} disabled={pushing || selectedRows().length === 0 || !instantlyStatus.connected} data-testid="lf-push-instantly" style={{ borderRadius: 2 }}>
                <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Push {selectedRows().length || ""} to Instantly
              </Button>
            </div>
          </div>

          <div className="border border-border bg-card overflow-x-auto" style={{ borderRadius: 2 }}>
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/40">
                <tr>
                  <th className="text-left px-3 py-2 w-8">
                    <Checkbox
                      checked={results.length > 0 && results.every((r) => selected[rowKey(r)])}
                      onCheckedChange={toggleAll}
                      data-testid="lf-select-all"
                    />
                  </th>
                  <th className="text-left px-3 py-2 overline">Name</th>
                  <th className="text-left px-3 py-2 overline">Title</th>
                  <th className="text-left px-3 py-2 overline">Company</th>
                  <th className="text-left px-3 py-2 overline">Industry</th>
                  <th className="text-right px-3 py-2 overline">Empl.</th>
                  <th className="text-left px-3 py-2 overline">Email</th>
                  <th className="text-left px-3 py-2 overline">Location</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-12 text-center text-sm text-muted-foreground" data-testid="lf-empty">
                    {loading ? `Searching ${provider === "apollo" ? "Apollo" : "Lusha"}…` : `No results yet — set filters and hit Search ${provider === "apollo" ? "Apollo" : "Lusha"}.`}
                  </td></tr>
                )}
                {results.map((r) => {
                  const id = rowKey(r);
                  return (
                    <tr key={id} className="border-b border-border hover:bg-secondary/30" data-testid={`lf-row-${id}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={!!selected[id]}
                          onCheckedChange={() => setSelected((s) => ({ ...s, [id]: !s[id] }))}
                          data-testid={`lf-check-${id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium whitespace-nowrap">{r.full_name || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.title || "—"}</td>
                      <td className="px-3 py-2">{r.company || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.industry || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{r.employees || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {provider === "lusha"
                          ? <span className="text-muted-foreground italic">reveal on import</span>
                          : r.email && !String(r.email).startsWith("email_not_unlocked")
                            ? r.email
                            : <span className="text-muted-foreground italic">locked</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{r.city || r.country || r.location || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {r.linkedin_url && (
                          <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground inline-flex items-center" data-testid={`lf-li-${id}`}>
                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <Button size="sm" variant="outline" onClick={() => runSearch(Math.max(1, page - 1))} disabled={page <= 1 || loading} data-testid="lf-prev" style={{ borderRadius: 2 }}>← Prev</Button>
              <div className="text-xs text-muted-foreground">Page {page} / {pagination.total_pages}</div>
              <Button size="sm" variant="outline" onClick={() => runSearch(page + 1)} disabled={page >= pagination.total_pages || loading} data-testid="lf-next" style={{ borderRadius: 2 }}>Next →</Button>
            </div>
          )}
          </>
          )}
        </section>
      </div>
    </Layout>
  );
}

function SalesNavPanel({ snUrl, setSnUrl, snMaxResults, setSnMaxResults, snAutoEnrich, setSnAutoEnrich, snAutoEnroll, setSnAutoEnroll, snRunning, setSnRunning, snRun, setSnRun, apifyConnected, lushaConnected }) {
  const [saved, setSaved] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);
  const [mode, setMode] = useState("filters");  // "filters" | "url"
  const [filters, setFilters] = useState({
    title_ids: [68],            // CFO by default
    region_ids: [101452733],    // Australia
    hq_country_ids: [],
    industry_ids: [],
    company_sizes: ["D", "E", "F"],  // 51-200, 201-500, 501-1000
    seniority: [],
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveFreq, setSaveFreq] = useState("daily");
  const [savingNow, setSavingNow] = useState(false);

  const loadSaved = () => api.get("/apify/saved-searches").then((r) => setSaved(r.data || [])).catch(() => {});
  useEffect(() => {
    loadSaved();
    api.get("/apify/filter-taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);

  // Auto-build URL when filters change
  useEffect(() => {
    if (mode !== "filters") return;
    const hasAny = Object.values(filters).some((v) => Array.isArray(v) && v.length > 0);
    if (!hasAny) { setPreviewUrl(""); return; }
    api.post("/apify/build-url", filters).then((r) => setPreviewUrl(r.data.url)).catch(() => {});
  }, [filters, mode]);

  const toggleFilter = (key, val) => {
    setFilters((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const startScrape = async () => {
    if (!apifyConnected) { toast.error("Apify not connected — paste API token in Settings"); return; }
    const url = mode === "filters" ? previewUrl : snUrl;
    if (mode === "filters") {
      // Need at least one filter category populated
      const hasFilter = (filters?.title_ids?.length || 0)
        + (filters?.region_ids?.length || 0)
        + (filters?.hq_country_ids?.length || 0)
        + (filters?.industry_ids?.length || 0)
        + (filters?.company_sizes?.length || 0)
        + (filters?.seniority?.length || 0)
        + ((filters?.title_keywords || "").trim() ? 1 : 0);
      if (!hasFilter) { toast.error("Select at least one filter (job title, location, size or seniority)"); return; }
    } else if (!url || !url.includes("linkedin.com/sales/search")) {
      toast.error("Paste a valid Sales Nav URL (or switch to Filter builder)");
      return;
    }
    setSnRunning(true);
    try {
      const body = {
        max_results: Number(snMaxResults) || 50,
        auto_enrich: !!snAutoEnrich,
        auto_enroll: !!snAutoEnroll,
      };
      if (mode === "filters") {
        body.filters = filters;
        body.search_url = url; // for legacy lead_radar actor; harvestapi ignores it
      } else {
        body.search_url = url;
      }
      const { data } = await api.post("/apify/sales-nav/run", body);
      setSnRun(data);
      toast.success(`Scrape started · Apify run ${data.apify_run_id?.slice(0, 8)}…`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Apify scrape");
    } finally {
      setSnRunning(false);
    }
  };

  const saveAndSchedule = async () => {
    if (!saveName.trim()) { toast.error("Give it a name (e.g. 'QLD CFOs')"); return; }
    setSavingNow(true);
    try {
      const body = {
        name: saveName.trim(),
        frequency: saveFreq,
        max_results: Number(snMaxResults) || 50,
        auto_enrich: !!snAutoEnrich,
        auto_enroll: !!snAutoEnroll,
        active: true,
      };
      if (mode === "filters") body.filters = filters;
      else body.search_url = snUrl;
      await api.post("/apify/saved-searches", body);
      toast.success(`Saved · will run ${freqLabel(saveFreq)} automatically`);
      setShowSaveForm(false);
      setSaveName("");
      loadSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingNow(false);
    }
  };

  const toggleActive = async (ss) => {
    try {
      await api.patch(`/apify/saved-searches/${ss.saved_search_id}`, { active: !ss.active });
      loadSaved();
    } catch (err) {
      toast.error("Could not update");
    }
  };

  const runNow = async (ss) => {
    try {
      const { data } = await api.post(`/apify/saved-searches/${ss.saved_search_id}/run-now`);
      toast.success(`Triggered '${ss.name}' — Apify run ${data.run.apify_run_id?.slice(0, 8)}…`);
      setSnRun(data.run);
      loadSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed");
    }
  };

  const editSearch = (ss) => {
    if (ss.filters) {
      setFilters({
        title_ids: ss.filters.title_ids || [],
        region_ids: ss.filters.region_ids || [],
        hq_country_ids: ss.filters.hq_country_ids || [],
        industry_ids: ss.filters.industry_ids || [],
        company_sizes: ss.filters.company_sizes || [],
        seniority: ss.filters.seniority || [],
      });
      setMode("filters");
    } else {
      setSnUrl(ss.search_url);
      setMode("url");
    }
    setSnMaxResults(ss.max_results);
    setSnAutoEnrich(ss.auto_enrich);
    setSnAutoEnroll(ss.auto_enroll);
    setSaveName(ss.name);
    setSaveFreq(ss.frequency);
    setShowSaveForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Editing '${ss.name}' — modify filters then click Save + activate. The old saved search will remain; delete it manually if you want to replace.`);
  };

  const deleteSearch = async (ss) => {
    if (!confirm(`Delete saved search '${ss.name}'?`)) return;
    try {
      await api.delete(`/apify/saved-searches/${ss.saved_search_id}`);
      toast.success("Deleted");
      loadSaved();
    } catch {
      toast.error("Delete failed");
    }
  };

  const activatePreset = async (presetKey, label) => {
    if (!apifyConnected) { toast.error("Apify not connected — paste API token in Settings first."); return; }
    try {
      const { data } = await api.post(`/apify/saved-searches/presets/${presetKey}`);
      toast.success(data.status === "reactivated" ? `${label} reactivated` : `${label} activated · runs daily`);
      loadSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not activate preset");
    }
  };

  return (
    <div className="space-y-4">
      {/* Saved searches */}
      <div className="bg-card border border-border p-5" style={{ borderRadius: 2 }} data-testid="sn-saved-panel">
        <div className="mb-3">
          <div className="overline">Saved searches (run automatically)</div>
          <div className="text-xs text-muted-foreground">Build a filter below, hit <strong className="text-foreground">Save + schedule</strong>, and we'll re-run it on your cadence. Edit any saved search — no Sales Nav round-trip ever.</div>
        </div>
        {saved.length === 0 ? (
          <div className="space-y-3 py-2">
            <div className="text-xs text-muted-foreground italic text-center">No saved searches yet.</div>
            <div className="border border-dashed border-foreground/30 p-3 bg-foreground/[0.02]" style={{ borderRadius: 2 }} data-testid="sn-preset-callout">
              <div className="text-xs font-medium mb-1">One-click recommended preset · LinkedIn-only mode</div>
              <div className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
                Australia · Finance leaders (CFO / Finance Director / Head of Finance / Controller / VP Finance) ·
                C-suite + Director seniority · 51–1,000 employee companies · runs <strong>every day</strong> ·
                auto-enrolls into the <strong>5-step LinkedIn-only sequence</strong> (profile view → connect → 3 DMs).
                <br/>
                <span className="text-[hsl(var(--success))] font-medium">Lusha is skipped</span> — saves credits since LinkedIn DMs don't need email/phone. Use the
                <em> Full funnel</em> preset below if you want email + voicemail too.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => activatePreset("au_cfo_daily", "Daily AU CFO search (LinkedIn-only)")}
                  disabled={!apifyConnected}
                  className="text-xs px-3 py-1.5 bg-foreground text-background hover:opacity-90 disabled:opacity-40"
                  style={{ borderRadius: 2 }}
                  data-testid="sn-preset-activate-au-cfo-daily"
                >
                  ⚡ Activate daily AU CFO (LinkedIn-only)
                </button>
                <button
                  onClick={() => activatePreset("au_cfo_full_funnel", "Daily AU CFO search (full funnel)")}
                  disabled={!apifyConnected || !lushaConnected}
                  className="text-xs px-3 py-1.5 border border-border hover:bg-secondary disabled:opacity-40"
                  style={{ borderRadius: 2 }}
                  data-testid="sn-preset-activate-au-cfo-full-funnel"
                >
                  Activate daily AU CFO (FULL funnel — requires Lusha)
                </button>
                {!apifyConnected && (
                  <span className="text-[10px] text-muted-foreground italic">Connect Apify in Settings first.</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {saved.map((ss) => (
              <div key={ss.saved_search_id} className="border border-border p-3 flex items-start justify-between gap-3" style={{ borderRadius: 2 }} data-testid={`sn-saved-${ss.saved_search_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-sm">{ss.name}</strong>
                    <span className={`text-[10px] px-1.5 py-0.5 ${ss.active ? "bg-[hsl(var(--success))]/20 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground"}`} style={{ borderRadius: 2 }}>
                      {ss.active ? "active" : "paused"}
                    </span>
                    <span className="text-xs text-muted-foreground">· {freqLabel(ss.frequency)} · {ss.max_results} max</span>
                  </div>
                  {ss.filters ? (
                    <FilterSummary filters={ss.filters} taxonomy={taxonomy} />
                  ) : (
                    <div className="text-[11px] font-mono text-muted-foreground truncate" title={ss.search_url}>{ss.search_url}</div>
                  )}
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {ss.last_run_at ? <>Last: {new Date(ss.last_run_at).toLocaleString()} · </> : null}
                    {ss.next_run_at ? <>Next: {new Date(ss.next_run_at).toLocaleString()} · </> : null}
                    Runs: <span className="font-mono">{ss.run_count}</span>
                  </div>
                  {ss.last_error && <div className="text-[11px] text-destructive truncate" title={ss.last_error}>⚠ {ss.last_error}</div>}
                </div>
                <div className="flex flex-col gap-1 text-xs">
                  <button onClick={() => runNow(ss)} className="underline hover:text-foreground" data-testid={`sn-saved-run-${ss.saved_search_id}`}>Run now</button>
                  <button onClick={() => editSearch(ss)} className="underline hover:text-foreground" data-testid={`sn-saved-edit-${ss.saved_search_id}`}>Edit</button>
                  <button onClick={() => toggleActive(ss)} className="underline hover:text-foreground" data-testid={`sn-saved-toggle-${ss.saved_search_id}`}>{ss.active ? "Pause" : "Resume"}</button>
                  <button onClick={() => deleteSearch(ss)} className="underline hover:text-destructive" data-testid={`sn-saved-delete-${ss.saved_search_id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New search */}
      <div className="bg-card border border-border p-5 space-y-4" style={{ borderRadius: 2 }} data-testid="sn-panel">
        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => setMode("filters")} className={`px-2 py-1 border ${mode === "filters" ? "bg-foreground text-background border-foreground" : "border-border"}`} style={{ borderRadius: 2 }} data-testid="sn-mode-filters">Filter builder</button>
          <button onClick={() => setMode("url")} className={`px-2 py-1 border ${mode === "url" ? "bg-foreground text-background border-foreground" : "border-border"}`} style={{ borderRadius: 2 }} data-testid="sn-mode-url">Paste URL</button>
        </div>

        {mode === "filters" && taxonomy && (
          <div className="space-y-4">
            <FilterChips label="Job title (CURRENT)" items={taxonomy.titles} selected={filters.title_ids} valueKey="id" onToggle={(v) => toggleFilter("title_ids", v)} testid="sn-f-title" />
            <FilterChips label="Person location (REGION)" items={taxonomy.geographies} selected={filters.region_ids} valueKey="id" onToggle={(v) => toggleFilter("region_ids", v)} testid="sn-f-region" />
            <FilterChips label="Company HQ country" items={taxonomy.geographies.slice(0, 10)} selected={filters.hq_country_ids} valueKey="id" onToggle={(v) => toggleFilter("hq_country_ids", v)} testid="sn-f-hq" />
            <FilterChips label="Industry" items={taxonomy.industries} selected={filters.industry_ids} valueKey="id" onToggle={(v) => toggleFilter("industry_ids", v)} testid="sn-f-industry" />
            <FilterChips label="Company size" items={taxonomy.company_sizes} selected={filters.company_sizes} valueKey="code" onToggle={(v) => toggleFilter("company_sizes", v)} testid="sn-f-size" />
            <FilterChips label="Seniority" items={taxonomy.seniority} selected={filters.seniority} valueKey="code" onToggle={(v) => toggleFilter("seniority", v)} testid="sn-f-seniority" />
            {previewUrl && (
              <details className="text-[11px] font-mono text-muted-foreground">
                <summary className="cursor-pointer text-xs">Preview generated URL ▾</summary>
                <div className="mt-1 break-all">{previewUrl}</div>
              </details>
            )}
          </div>
        )}

        {mode === "url" && (
          <div>
            <Label className="overline">Sales Navigator search URL</Label>
            <textarea
              value={snUrl}
              onChange={(e) => setSnUrl(e.target.value)}
              placeholder="Paste from LinkedIn Sales Nav address bar"
              rows={4}
              className="mt-1 w-full input-crisp font-mono text-xs px-3 py-2 bg-background border border-border"
              style={{ borderRadius: 2 }}
              data-testid="sn-url"
            />
            <div className="text-xs text-muted-foreground mt-1">Power-user mode — use this if you need filters not yet supported in the builder above.</div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="overline">Max results</Label>
            <Input type="number" min={1} max={500} value={snMaxResults} onChange={(e) => setSnMaxResults(e.target.value)} className="mt-1 input-crisp font-mono" data-testid="sn-max" />
          </div>
          <div className="flex items-end">
            <Button onClick={startScrape} disabled={snRunning || !apifyConnected} className="w-full" data-testid="sn-start" style={{ borderRadius: 2 }}>
              <Search className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {snRunning ? "Starting…" : "Run once"}
            </Button>
          </div>
        </div>

        {!showSaveForm ? (
          <Button variant="outline" onClick={() => setShowSaveForm(true)} className="w-full" data-testid="sn-save-toggle" style={{ borderRadius: 2 }}>
            + Save & schedule (run automatically)
          </Button>
        ) : (
          <div className="border border-border p-3 space-y-3" style={{ borderRadius: 2 }}>
            <div>
              <Label className="overline">Name</Label>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. QLD CFOs — Manufacturing" className="mt-1 input-crisp" data-testid="sn-save-name" />
            </div>
            <div>
              <Label className="overline">Frequency</Label>
              <Select value={saveFreq} onValueChange={setSaveFreq}>
                <SelectTrigger className="mt-1 input-crisp" data-testid="sn-save-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Every hour</SelectItem>
                  <SelectItem value="every_6h">Every 6 hours</SelectItem>
                  <SelectItem value="daily">Every day</SelectItem>
                  <SelectItem value="weekdays">Weekdays only</SelectItem>
                  <SelectItem value="weekly">Once a week</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveAndSchedule} disabled={savingNow} className="flex-1" data-testid="sn-save-confirm" style={{ borderRadius: 2 }}>
                {savingNow ? "Saving…" : "Save + activate"}
              </Button>
              <Button variant="outline" onClick={() => setShowSaveForm(false)} style={{ borderRadius: 2 }}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-border space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sn-auto-enrich-label">
            <Checkbox checked={snAutoEnrich} onCheckedChange={(v) => setSnAutoEnrich(!!v)} data-testid="sn-auto-enrich" disabled={!lushaConnected} />
            <span>Auto-enrich each row via Lusha (email + phone)</span>
          </label>
          {!lushaConnected && (
            <div className="text-xs text-[hsl(var(--warning))] pl-6">Lusha not connected — enrichment will be skipped. <Link to="/settings" className="underline">Add Lusha key</Link>.</div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sn-auto-enroll-label">
            <Checkbox checked={snAutoEnroll} onCheckedChange={(v) => setSnAutoEnroll(!!v)} data-testid="sn-auto-enroll" />
            <span>Auto-enrol every imported lead in the 11-step CFO sequence</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function FilterChips({ label, items, selected, valueKey, onToggle, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="overline mb-1 block">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => {
          const val = it[valueKey];
          const isOn = selected.includes(val);
          return (
            <button
              key={`${valueKey}-${val}-${it.label}`}
              onClick={() => onToggle(val)}
              className={`text-xs px-2 py-1 border transition ${isOn ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
              style={{ borderRadius: 2 }}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterSummary({ filters, taxonomy }) {
  if (!taxonomy) return null;
  const lookup = (items, key, val) => (items.find((x) => x[key] === val) || {}).label || val;
  const parts = [];
  if (filters.title_ids?.length) parts.push((filters.title_ids).map((v) => lookup(taxonomy.titles, "id", v)).join(", "));
  if (filters.region_ids?.length) parts.push((filters.region_ids).map((v) => lookup(taxonomy.geographies, "id", v)).join(", "));
  if (filters.industry_ids?.length) parts.push((filters.industry_ids).map((v) => lookup(taxonomy.industries, "id", v)).join(", "));
  if (filters.company_sizes?.length) parts.push((filters.company_sizes).map((v) => lookup(taxonomy.company_sizes, "code", v)).join(", "));
  if (filters.seniority?.length) parts.push((filters.seniority).map((v) => lookup(taxonomy.seniority, "code", v)).join(", "));
  return <div className="text-[11px] text-muted-foreground mt-0.5">{parts.join(" · ")}</div>;
}

function freqLabel(f) {
  return {
    hourly: "every hour", every_6h: "every 6h", daily: "daily",
    weekdays: "weekdays", weekly: "weekly",
  }[f] || f;
}

function SalesNavResults({ snRun, snRuns, lushaConnected }) {
  const [queue, setQueue] = useState(null);
  useEffect(() => {
    const load = () => api.get("/apify/queue-status").then((r) => setQueue(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30_000);  // refresh every 30s
    return () => clearInterval(t);
  }, [snRun]);

  if (!snRun && (!snRuns || snRuns.length === 0)) {
    return (
      <div className="space-y-4">
        {queue && queue.pending_enrichment > 0 && <AutoEnrichBanner queue={queue} />}
        <div className="border border-border bg-card p-12 text-center text-sm text-muted-foreground" style={{ borderRadius: 2 }} data-testid="sn-empty">
          <div className="font-serif text-lg mb-2 text-foreground" style={{ fontWeight: 450 }}>No scrapes yet</div>
          Paste a Sales Nav search URL on the left, hit <strong className="text-foreground">Run Sales Nav scrape</strong>, and we'll handle the rest: scrape → Lusha-enrich → import → enrol in your 11-step CFO sequence.
          {!lushaConnected && <div className="mt-3 text-[hsl(var(--warning))]">Tip: connect Lusha for verified emails — without it, enrichment will be skipped and imports may have no email.</div>}
        </div>
      </div>
    );
  }

  const active = snRun;
  const recent = (snRuns || []).filter((r) => !active || r.run_id !== active.run_id).slice(0, 5);

  return (
    <div className="space-y-4">
      {queue && (queue.pending_enrichment > 0 || queue.lusha_cooldown_seconds > 0) && <AutoEnrichBanner queue={queue} />}
      {active && <RunCard run={active} active />}
      {recent.length > 0 && (
        <div>
          <div className="overline mb-2">Recent runs</div>
          <div className="space-y-2">
            {recent.map((r) => <RunCard key={r.run_id} run={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function AutoEnrichBanner({ queue }) {
  const cooldownMins = Math.ceil((queue.lusha_cooldown_seconds || 0) / 60);
  return (
    <div className="border border-border bg-card p-4" style={{ borderRadius: 2 }} data-testid="sn-auto-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline mb-1">Auto-enrichment queue</div>
          <div className="text-sm">
            <strong className="text-foreground font-mono text-base">{queue.pending_enrichment}</strong> scraped CFOs are queued for Lusha email lookup.{" "}
            {queue.lusha_cooldown_seconds > 0 ? (
              <span className="text-[hsl(var(--warning))]">Lusha is rate-limited — resuming automatically in ~{cooldownMins} min.</span>
            ) : (
              <span className="text-muted-foreground">Hybrid mode: leads enter the LinkedIn sequence immediately (invites/InMails start now). When Lusha returns an email, they auto-upgrade to the full multi-channel sequence.</span>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          Imported so far: <span className="font-mono text-foreground">{queue.imported}</span><br/>
          No-match (final): <span className="font-mono">{queue.no_email_final}</span>
        </div>
      </div>
    </div>
  );
}

function RunCard({ run, active }) {
  const [showScraped, setShowScraped] = useState(false);
  const [scrapedRows, setScrapedRows] = useState(null);
  const [reEnriching, setReEnriching] = useState(false);
  const [reRunning, setReRunning] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const s = run.stats || {};
  const statusColor = run.status === "succeeded" ? "text-[hsl(var(--success))]"
    : run.status === "failed" ? "text-destructive"
    : "text-[hsl(var(--warning))]";

  const loadScraped = async () => {
    setShowScraped(true);
    if (scrapedRows) return;
    try {
      const { data } = await api.get(`/apify/sales-nav/runs/${run.run_id}/scraped-rows`);
      setScrapedRows(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load scraped rows");
    }
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/apify/sales-nav/runs/${run.run_id}/scraped.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `apify_scraped_${run.run_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("CSV download failed");
    }
  };

  const reEnrich = async () => {
    setReEnriching(true);
    try {
      const { data } = await api.post(`/apify/sales-nav/runs/${run.run_id}/re-enrich`);
      const r = data.re_stats || {};
      if (r.attempted === 0) {
        toast.info("Nothing left to enrich — all rows already imported or errored.");
      } else if (data.lusha_disabled_mid_run) {
        toast.error(`Lusha bailed mid-batch: tried ${r.attempted}, enriched ${r.enriched}, errors ${r.errors}. Wait for rate-limit reset and try again.`);
      } else {
        toast.success(`Re-enriched ${r.enriched} of ${r.attempted} · imported ${r.imported}`);
      }
      setScrapedRows(null);
      if (showScraped) loadScraped();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-enrich failed");
    } finally {
      setReEnriching(false);
    }
  };

  const reRun = async () => {
    setReRunning(true);
    try {
      const { data } = await api.post(`/apify/sales-nav/runs/${run.run_id}/re-run`);
      toast.success(`Re-run started · new Apify run ${data.new_run.apify_run_id?.slice(0, 8)}…`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-run failed");
    } finally {
      setReRunning(false);
    }
  };

  const deleteRun = async () => {
    if (!confirm("Delete this run from the list? Imported leads are kept.")) return;
    try {
      await api.delete(`/apify/sales-nav/runs/${run.run_id}`);
      setDeleted(true);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (deleted) return null;

  const zeroResult = run.status === "succeeded" && (s.scraped || 0) === 0;

  return (
    <div className={`border border-border bg-card p-4 ${active ? "ring-1 ring-primary/30" : ""}`} style={{ borderRadius: 2 }} data-testid={`sn-run-${run.run_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-mono text-muted-foreground truncate max-w-md" title={run.search_url}>
          {run.search_url}
        </div>
        <div className={`text-xs font-medium ${statusColor}`} data-testid={`sn-status-${run.run_id}`}>
          {run.status === "running" || run.status === "processing" ? `${run.status}… (Apify: ${run.apify_status})` : run.status}
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3 text-xs">
        <Stat label="Scraped" v={s.scraped} />
        <Stat label="Enriched" v={s.enriched} />
        <Stat label="Imported" v={s.imported} />
        <Stat label="Duplicates" v={s.duplicates} />
        <Stat label="No-email" v={s.no_email} />
        <Stat label="Enroled" v={s.enrolled} hi />
      </div>
      {run.error && <div className="mt-2 text-xs text-destructive">{run.error}</div>}
      {(run.status === "running" || run.status === "processing") && (
        <div className="mt-2 text-xs text-muted-foreground italic">Polling every 5s — feel free to leave this page. The scheduler will finish + auto-import in the background.</div>
      )}
      {zeroResult && (
        <div className="mt-2 text-xs text-[hsl(var(--warning))] italic">
          0 results — the auto-retry scheduler will re-fire this in ~15min if eligible, or click Re-run now.
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
        {(s.scraped || 0) > 0 && (
          <>
            <button onClick={loadScraped} className="underline hover:text-foreground" data-testid={`sn-view-scraped-${run.run_id}`}>
              View {s.scraped} scraped {showScraped ? "▾" : "▸"}
            </button>
            <span className="text-muted-foreground">·</span>
            <button onClick={downloadCsv} className="underline hover:text-foreground" data-testid={`sn-csv-${run.run_id}`}>
              Download CSV
            </button>
            <span className="text-muted-foreground">·</span>
            <button onClick={reEnrich} disabled={reEnriching} className="underline hover:text-foreground disabled:opacity-50" data-testid={`sn-reenrich-${run.run_id}`}>
              {reEnriching ? "Re-enriching…" : "Retry Lusha enrichment"}
            </button>
          </>
        )}
        {(run.status === "succeeded" || run.status === "failed") && (
          <>
            {(s.scraped || 0) > 0 && <span className="text-muted-foreground">·</span>}
            <button onClick={reRun} disabled={reRunning} className="underline hover:text-foreground disabled:opacity-50" data-testid={`sn-rerun-${run.run_id}`}>
              {reRunning ? "Starting…" : "Re-run"}
            </button>
            <span className="text-muted-foreground">·</span>
            <button onClick={deleteRun} className="underline hover:text-destructive" data-testid={`sn-delete-${run.run_id}`}>
              Delete
            </button>
          </>
        )}
        {run.status === "succeeded" && (s.imported || 0) > 0 && (
          <>
            <span className="text-muted-foreground">·</span>
            <Link to="/leads" className="underline hover:text-foreground" data-testid={`sn-leads-link-${run.run_id}`}>→ View imported leads</Link>
          </>
        )}
      </div>

      {showScraped && scrapedRows && (
        <ScrapedRowsTable data={scrapedRows} />
      )}
    </div>
  );
}

function ScrapedRowsTable({ data }) {
  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="text-xs text-muted-foreground mb-2 flex flex-wrap items-center gap-2">
        <strong className="text-foreground">{data.total}</strong> rows ·
        {Object.entries(data.by_status || {}).map(([k, v]) => (
          <span key={k} className="font-mono">{k}: <span className="text-foreground">{v}</span></span>
        ))}
      </div>
      <div className="max-h-96 overflow-auto border border-border" style={{ borderRadius: 2 }}>
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              <th className="text-left p-2 font-medium">Name</th>
              <th className="text-left p-2 font-medium">Title</th>
              <th className="text-left p-2 font-medium">Company</th>
              <th className="text-left p-2 font-medium">Location</th>
              <th className="text-left p-2 font-medium">Email</th>
              <th className="text-left p-2 font-medium">Phone</th>
              <th className="text-left p-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.scraped_row_id} className="border-t border-border hover:bg-muted/20">
                <td className="p-2">
                  {r.linkedin_url ? (
                    <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="underline">{r.full_name || "(no name)"}</a>
                  ) : (r.full_name || "(no name)")}
                </td>
                <td className="p-2 text-muted-foreground">{r.title || ""}</td>
                <td className="p-2 text-muted-foreground">{r.company || ""}</td>
                <td className="p-2 text-muted-foreground">{r.location || ""}</td>
                <td className="p-2 font-mono">{r.email || "—"}</td>
                <td className="p-2 font-mono">{r.phone || "—"}</td>
                <td className="p-2">
                  <span className={
                    r.enrichment_status === "imported" ? "text-[hsl(var(--success))]"
                    : r.enrichment_status === "duplicate" ? "text-muted-foreground"
                    : r.enrichment_status?.startsWith("error") ? "text-destructive"
                    : "text-[hsl(var(--warning))]"
                  }>{r.enrichment_status || "?"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, v, hi }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`font-mono text-lg ${hi ? "text-foreground" : "text-muted-foreground"}`} style={{ fontWeight: 450 }}>{v ?? 0}</div>
    </div>
  );
}

function ConnectionPill({ label, ok, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <div className={`h-2 w-2 ${ok ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`} style={{ borderRadius: 999 }} />
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">{ok ? "connected" : "not connected — add key in Settings"}</span>
    </div>
  );
}
